import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useChurch, useChurchEvents, useChurchStats } from '../hooks/useChurches'
import { useSubscriptionStatus, useUpdateOccurrence, useChurchMembers } from '../hooks/useChurchAdmin'
import { useChurchDrafts } from '../hooks/useChurches'
import { useAuthStore } from '../store/auth'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard, AdmBtn, Segmented } from '../components/admin/AdminAtoms'
import { AdmIcn } from '../components/admin/AdminIcons'
import { getEventType } from '../components/ui/eventTypes'
import { CampusManagerCard } from '../components/admin/CampusManagerCard'
import { BulletinImportModal } from '../components/admin/BulletinImportModal'
import { Avatar } from '../components/ui/Avatar'
import type { OccurrenceWithEvent } from '@fellowship/shared'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function formatDateBlock(iso: string): { dow: string; day: string; mon: string } {
  const d = new Date(iso)
  return {
    dow: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
    day: String(d.getDate()),
    mon: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
  }
}

// ─── KPI tile ────────────────────────────────────────────────────────────────

function DashKpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: 'good' | 'warn' }) {
  const subClass = tone === 'warn' ? 'text-warn' : tone === 'good' ? 'text-good' : 'text-muted'
  return (
    <div className="p-4 rounded-2xl bg-surface border border-line-soft">
      <div className="eyebrow mb-2">{label}</div>
      <div className="serif text-[32px] leading-none tracking-[-0.02em]">{value}</div>
      <div className={`mt-1.5 text-[11.5px] ${subClass}`}>{sub}</div>
    </div>
  )
}

// ─── Event row ───────────────────────────────────────────────────────────────

type GroupedOccurrence = { next: OccurrenceWithEvent; moreCount: number }

function groupByEvent(occs: OccurrenceWithEvent[]): GroupedOccurrence[] {
  const map = new Map<string, GroupedOccurrence>()
  for (const occ of occs) {
    const key = occ.event.id
    if (!map.has(key)) map.set(key, { next: occ, moreCount: 0 })
    else map.get(key)!.moreCount++
  }
  return Array.from(map.values())
}

function DashEventRow({ occ, first, slug, moreCount = 0, readOnly = false }: { occ: OccurrenceWithEvent; first: boolean; slug: string; moreCount?: number; readOnly?: boolean }) {
  const t = getEventType(occ.event.eventType)
  const db = formatDateBlock(occ.startsAt)
  const progress = occ.dishProgress
  const navigate = useNavigate()

  const totalGoal = progress?.goals
    ? Object.values(progress.goals as Record<string, number>).reduce((a, b) => a + b, 0)
    : 0
  const totalClaimed = progress?.claimed
    ? Object.values(progress.claimed as Record<string, number>).reduce((a, b) => a + b, 0)
    : 0
  const ratio = totalGoal > 0 ? totalClaimed / totalGoal : null

  return (
    <div className={`flex items-center gap-3.5 px-[18px] py-3.5 ${first ? '' : 'border-t border-line-soft'}`}>
      {/* Date block */}
      <div className="w-[46px] text-center shrink-0">
        <div className="mono text-[9.5px] text-muted tracking-[0.08em]">{db.dow}</div>
        <div className="serif text-[22px] leading-none tracking-[-0.02em]">{db.day}</div>
        <div className="mono text-[9px] text-muted mt-0.5">{db.mon}</div>
      </div>

      <div
        className="w-7 h-7 rounded-lg shrink-0 inline-flex items-center justify-center"
        style={{ background: t.soft, color: t.color }}
      ><t.icon size={14} strokeWidth={1.9} /></div>

      {/* Title + meta */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          {readOnly ? (
            <span className="text-[14.5px] font-medium truncate text-ink">{occ.event.title}</span>
          ) : (
            <Link
              to={`/churches/${slug}/events/${occ.event.id}/edit`}
              className="text-[14.5px] font-medium truncate text-ink no-underline hover:underline"
            >
              {occ.event.title}
            </Link>
          )}
          {occ.event.isRecurring && (
            <span className="text-[9.5px] px-[7px] py-0.5 rounded bg-s2 text-muted font-mono tracking-[0.08em] shrink-0">
              RECURRING{moreCount > 0 ? ` · +${moreCount} more` : ''}
            </span>
          )}
        </div>
        <div className="mt-[3px] text-[11.5px] text-muted flex gap-2 overflow-hidden">
          <span className="shrink-0">{formatTime(occ.startsAt)}</span>
          {(occ.overrideAddressText ?? occ.event.addressText) && (
            <>
              <span className="shrink-0">·</span>
              <span className="truncate">{occ.overrideAddressText ?? occ.event.addressText}</span>
            </>
          )}
        </div>
      </div>

      {/* Dish coverage mini-bar — only shown for potluck events */}
      {ratio !== null && (
        <div className="w-[90px] shrink-0">
          <div className={`mono text-[9.5px] mb-[3px] text-right ${ratio < 0.5 ? 'text-[#A33A2A]' : 'text-muted'}`}>
            {totalClaimed}/{totalGoal} dishes
          </div>
          <div className="h-1.5 rounded-[3px] bg-s2 border border-line-soft relative">
            <div
              className="absolute inset-0 rounded-[3px]"
              style={{
                right: `${(1 - ratio) * 100}%`,
                background: ratio >= 0.85 ? 'var(--good)' : ratio < 0.5 ? '#A33A2A' : 'var(--warn)',
              }}
            />
          </div>
        </div>
      )}

      {/* RSVP count */}
      <div className="w-[44px] text-center shrink-0">
        <div className="text-[16px] font-semibold">{occ.rsvpCount ?? 0}</div>
        <div className="mono text-[9px] text-muted">RSVPS</div>
      </div>

      {/* Actions */}
      <div className="flex gap-1.5 shrink-0">
        <Link to={`/events/${occ.event.id}`} target="_blank" className="no-underline">
          <AdmBtn size="sm" variant="ghost">View ↗</AdmBtn>
        </Link>
        {!readOnly && (
          <>
            <Link to={`/churches/${slug}/events/${occ.event.id}/edit`} className="no-underline">
              <AdmBtn size="sm" variant="ghost">Edit</AdmBtn>
            </Link>
            <AdmBtn size="sm" variant="ghost" onClick={() => navigate(`/churches/${slug}/occurrences/${occ.id}/overview`)}>
              Manage <AdmIcn name="chev" size={11} />
            </AdmBtn>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Calendar feed card ───────────────────────────────────────────────────────

function CalendarFeedCard({ slug }: { slug: string }) {
  return (
    <AdminCard>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="serif text-[17px] mb-1">Calendar sync</div>
          <p className="text-[12.5px] text-ink-2 m-0">
            Subscribe to your Fellowship events in Google Calendar, Apple Calendar, or Outlook — or import events from Google Calendar.
          </p>
        </div>
        <Link
          to={`/churches/${slug}/calendar`}
          className="shrink-0 self-center px-3 py-1.5 rounded-lg border border-line bg-s2 text-[12px] font-semibold text-ink-2 no-underline whitespace-nowrap"
        >
          Set up →
        </Link>
      </div>
    </AdminCard>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ChurchDashboardPage() {
  const { slug } = useParams<{ slug: string }>()
  const { user } = useAuthStore()
  const { data: churchData, isLoading: churchLoading } = useChurch(slug)
  const church = churchData?.data
  const { data: potlucksData, isLoading: eventsLoading } = useChurchEvents(slug)
  const { data: subData } = useSubscriptionStatus(church?.id)
  const { data: membersData } = useChurchMembers(church?.id)
  useChurchStats(slug, Boolean(church?.id))
  useUpdateOccurrence(church?.id ?? '')

  const [tab, setTab] = useState('Upcoming')
  const [showBulletinImport, setShowBulletinImport] = useState(false)
  const { data: draftsData } = useChurchDrafts(church?.id)

  const isOwner = membersData?.data.members.some(
    (m) => m.userId === user?.id && m.role === 'owner',
  ) ?? false

  const allOccurrences = potlucksData?.data ?? []
  const now = new Date()
  const upcomingOccurrences = allOccurrences.filter((o) => !o.isCancelled && new Date(o.startsAt) >= now)
  const pastOccurrences = allOccurrences.filter((o) => !o.isCancelled && new Date(o.startsAt) < now)
  const groupedUpcoming = groupByEvent(upcomingOccurrences)
  const groupedPast = groupByEvent(pastOccurrences)
  const grouped = tab === 'Past' ? groupedPast : groupedUpcoming
  const potluckOccs = upcomingOccurrences.filter((o) => o.dishProgress != null)
  const subscription = subData?.data
  const subscriptionActive = subscription?.status === 'active' || subscription?.status === 'trialing'

  const firstName = user?.displayName?.split(' ')[0] ?? 'there'

  const upcomingCount = groupedUpcoming.length
  const rsvpTotal = upcomingOccurrences.reduce((s, o) => s + (o.rsvpCount ?? 0), 0)
  const memberCount = membersData?.data.members.length ?? 0

  const followerCount = church?.followerCount ?? 0

  if (churchLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-[13px] text-muted">Loading…</div>
      </div>
    )
  }

  if (!church) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-[13px] text-muted">Church not found.</div>
      </div>
    )
  }

  return (
    <AdminShell>
      <AdminPageHeader
        eyebrow={`${church.name} · dashboard`}
        title="Good morning,"
        titleItalic={`${firstName}.`}
        sub={`${upcomingCount} event${upcomingCount !== 1 ? 's' : ''} on the calendar · ${rsvpTotal} RSVPs total`}
        actions={
          <>
            {subscriptionActive && church?.claimed && (
              <>
                <AdmBtn icon="doc" variant="ghost" onClick={() => setShowBulletinImport(true)}>
                  Import bulletin
                </AdmBtn>
                <Link to={`/churches/${slug}/events/new`} className="no-underline">
                  <AdmBtn icon="plus" variant="primary">New event</AdmBtn>
                </Link>
              </>
            )}
          </>
        }
      />

      {/* Verification pending banner */}
      {!church?.claimed && (
        <div className="mb-5 px-5 py-[18px] rounded-[14px] bg-[#eff6ff] border border-[#bfdbfe]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[13.5px] font-semibold text-[#1d4ed8]">Verification pending</div>
              <div className="text-[12px] mt-0.5 leading-[1.5] text-[#3b82f6]">
                Your church page is under review. For now you have a read-only preview of your dashboard — editing your profile, managing your team, subscribing, and posting events all unlock once our team approves your claim (usually within 1–2 business days).
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval pending / rejected banner (issue #179: self-serve churches
          are reviewed before going public) */}
      {church?.claimed && church.approvalStatus === 'pending' && (
        <div className="mb-5 px-5 py-[18px] rounded-[14px] bg-[#eff6ff] border border-[#bfdbfe]">
          <div className="text-[13.5px] font-semibold text-[#1d4ed8]">Pending review</div>
          <div className="text-[12px] mt-0.5 leading-[1.5] text-[#3b82f6]">
            Thanks for adding your church. It's under review and isn't public yet — it won't appear in search or on the map, and events can't be posted until our team approves it (usually within 1–2 business days). You can finish setting up your profile in the meantime.
          </div>
        </div>
      )}
      {church?.claimed && church.approvalStatus === 'rejected' && (
        <div className="mb-5 px-5 py-[18px] rounded-[14px] bg-[#fef2f2] border border-[#fecaca]">
          <div className="text-[13.5px] font-semibold text-[#b91c1c]">Not approved</div>
          <div className="text-[12px] mt-0.5 leading-[1.5] text-[#ef4444]">
            This church page was not approved and remains private. If you believe this was a mistake, reply to the email we sent or contact support.
          </div>
        </div>
      )}

      {/* Subscription warning */}
      {church?.claimed && church.approvalStatus === 'approved' && !subscriptionActive && (
        <div className="mb-5 px-5 py-[18px] rounded-[14px] bg-ochre-soft border border-ochre-line">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[13.5px] font-semibold text-ochre">Activate your church page</div>
              <div className="text-[12px] mt-0.5 leading-[1.5] text-ochre-muted">
                Your page is live. Subscribe to post events and manage RSVPs, dish signups, and event broadcasts.
              </div>
            </div>
            <Link to={`/churches/${slug}/billing`} className="no-underline shrink-0">
              <AdmBtn variant="ochre" size="sm">Start free trial</AdmBtn>
            </Link>
          </div>
          {(church.viewCount > 0 || church.planToAttendCount > 0) && (
            <div className="mt-3.5 pt-3.5 flex gap-7 border-t border-t-ochre-line">
              <div>
                <span className="text-[22px] font-bold font-serif text-ochre">{church.viewCount}</span>
                <span className="text-[12px] ml-1.5 text-ochre-muted">event views before you claimed</span>
              </div>
              <div>
                <span className="text-[22px] font-bold font-serif text-ochre">{church.planToAttendCount}</span>
                <span className="text-[12px] ml-1.5 text-ochre-muted">plan-to-attend taps</span>
              </div>
            </div>
          )}
        </div>
      )}

      {subscription?.cancelAtPeriodEnd && (
        <div className="mb-5 px-[18px] py-3.5 rounded-[14px] flex items-center justify-between gap-4 bg-danger-medium border border-danger-medium-line">
          <div>
            <div className="text-[13.5px] font-semibold text-danger">Subscription cancels soon</div>
            <div className="text-[12px] mt-0.5 opacity-80 text-danger">
              Your subscription ends {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'soon'}.
            </div>
          </div>
          <Link to={`/churches/${slug}/billing`} className="no-underline">
            <AdmBtn variant="danger" size="sm">Manage billing</AdmBtn>
          </Link>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <DashKpi label="Upcoming" value={String(upcomingCount)} sub="next 30 days" />
        <DashKpi label="RSVPs total" value={String(rsvpTotal)} sub="across all events" tone="good" />
        <DashKpi label="Team members" value={String(memberCount)} sub={isOwner ? 'you are owner' : 'coordinator'} />
        <DashKpi label="Followers" value={String(followerCount)} sub="following your page" tone={followerCount > 0 ? 'good' : undefined} />
      </div>

      {/* Main 2-col grid */}
      <div className="grid gap-5" style={{ gridTemplateColumns: '1.45fr 1fr' }}>

        {/* Drafts */}
        {draftsData?.data && draftsData.data.length > 0 && (
          <AdminCard padding={0} style={{ paddingBottom: 6, gridColumn: '1 / -1' }}>
            <div className="flex items-center justify-between px-[18px] pt-[18px] pb-3">
              <div className="flex items-center gap-2">
                <div className="serif text-[20px]">Drafts</div>
                <span className="px-2 py-0.5 rounded-full bg-s2 border border-line-soft text-[11px] text-muted font-semibold">{draftsData.data.length}</span>
              </div>
              <div className="text-[11.5px] text-muted">Not published — only you can see these</div>
            </div>
            {draftsData.data.map((draft) => (
              <Link key={draft.id} to={`/churches/${slug}/events/${draft.id}/edit`} className="no-underline">
                <div className="flex items-center justify-between px-[18px] py-3 border-t border-line-soft hover:bg-s2 transition-colors">
                  <div>
                    <div className="text-[14px] font-semibold text-ink">{draft.title}</div>
                    <div className="text-[12px] text-muted capitalize">{draft.eventType}</div>
                  </div>
                  <span className="text-[12px] text-primary font-medium">Edit & publish →</span>
                </div>
              </Link>
            ))}
          </AdminCard>
        )}

        {/* Upcoming events table */}
        <AdminCard padding={0} style={{ paddingBottom: 6 }}>
          <div className="flex items-baseline justify-between px-[18px] pt-[18px] pb-3">
            <div className="serif text-[22px]">Upcoming events</div>
            <Segmented value={tab} options={['Upcoming', 'Past']} onChange={setTab} />
          </div>

          {eventsLoading && (
            <div className="px-[18px] py-6 text-[13px] text-muted">Loading…</div>
          )}

          {!eventsLoading && grouped.length === 0 && (
            <div className="px-[18px] py-10 text-center">
              <div className="text-[13px] text-muted mb-4">
                {tab === 'Past' ? 'No past events.' : 'No upcoming events.'}
              </div>
              {tab === 'Upcoming' && subscriptionActive && church.claimed && (
                <Link to={`/churches/${slug}/events/new`} className="no-underline">
                  <AdmBtn variant="primary" icon="plus">Create your first event</AdmBtn>
                </Link>
              )}
            </div>
          )}

          {grouped.map(({ next, moreCount }, i) => (
            <DashEventRow key={next.event.id} occ={next} first={i === 0} slug={slug!} moreCount={moreCount} readOnly={!church.claimed} />
          ))}
        </AdminCard>

        {/* Right column */}
        <div className="flex flex-col gap-5">

          {/* Dish-gap attention items */}
          {potluckOccs.filter(o => {
            const goals = Object.values(o.dishProgress!.goals as Record<string, number>).reduce((a, b) => a + b, 0)
            const claimed = Object.values(o.dishProgress!.claimed as Record<string, number>).reduce((a, b) => a + b, 0)
            return goals > 0 && claimed / goals < 0.6
          }).length > 0 && (
            <AdminCard>
              <div className="flex items-center gap-1.5 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A33A2A]" />
                <div className="serif text-[17px]">Needs attention</div>
              </div>
              <div className="flex flex-col gap-2.5">
                {potluckOccs.filter(o => {
                  const goals = Object.values(o.dishProgress!.goals as Record<string, number>).reduce((a, b) => a + b, 0)
                  const claimed = Object.values(o.dishProgress!.claimed as Record<string, number>).reduce((a, b) => a + b, 0)
                  return goals > 0 && claimed / goals < 0.6
                }).map(o => {
                  const goals = Object.values(o.dishProgress!.goals as Record<string, number>).reduce((a, b) => a + b, 0)
                  const claimed = Object.values(o.dishProgress!.claimed as Record<string, number>).reduce((a, b) => a + b, 0)
                  return (
                    <div
                      key={o.id}
                      className="p-3 rounded-xl bg-s2 border border-line-soft"
                      style={{ borderLeft: '3px solid var(--warn)' }}
                    >
                      <div className="text-[12.5px] font-semibold mb-[3px]">{o.event.title}</div>
                      <div className="text-[12px] text-ink-2 leading-[1.4]">
                        Only {claimed}/{goals} dishes committed — send a dish gap reminder
                      </div>
                      <div className="mt-2 flex gap-2">
                        <Link to={`/churches/${slug}/occurrences/${o.id}/host`} className="no-underline">
                          <AdmBtn variant="primary" size="sm">Send dish gap</AdmBtn>
                        </Link>
                        <Link to={`/churches/${slug}/occurrences/${o.id}/overview`} className="no-underline">
                          <AdmBtn variant="ghost" size="sm">Manage</AdmBtn>
                        </Link>
                      </div>
                    </div>
                  )
                })}
              </div>
            </AdminCard>
          )}

          {/* Team summary */}
          <AdminCard title="Team">
            <div className="flex flex-col gap-2 mb-3">
              {(membersData?.data.members ?? []).slice(0, 3).map(member => (
                <div key={member.id} className="flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-s2 border border-line-soft">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <Avatar name={member.user?.displayName ?? member.user?.email} src={member.user?.avatarUrl} size={30} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium truncate">
                        {member.user?.displayName ?? member.user?.email}
                      </div>
                    </div>
                  </div>
                  <span className="text-[10.5px] px-2.5 py-[3px] rounded-full bg-surface border border-line-soft text-ink-2 capitalize font-medium shrink-0">
                    {member.role}
                  </span>
                </div>
              ))}
              {memberCount > 3 && (
                <div className="text-[12px] text-muted text-center py-1">+{memberCount - 3} more</div>
              )}
            </div>
            {church.claimed && (
              <Link to={`/churches/${slug}/members`} className="no-underline">
                <AdmBtn icon="users" variant="soft" block>Manage team</AdmBtn>
              </Link>
            )}
          </AdminCard>

          {/* Quick links — setup actions unavailable until the claim is verified */}
          {church.claimed && (
            <AdminCard>
              <div className="serif text-[17px] mb-3">Quick links</div>
              <div className="flex flex-col gap-1.5">
                <Link to={`/churches/${slug}/bulletin`} className="no-underline">
                  <AdmBtn icon="doc" variant="soft" block>Bulletin insert</AdmBtn>
                </Link>
                <Link to={`/churches/${slug}/edit`} className="no-underline">
                  <AdmBtn icon="church" variant="soft" block>Edit church profile</AdmBtn>
                </Link>
                <Link to={`/churches/${slug}/billing`} className="no-underline">
                  <AdmBtn icon="card" variant="soft" block>Billing & plan</AdmBtn>
                </Link>
              </div>
            </AdminCard>
          )}

          {/* Campuses */}
          {church.claimed && (
            <CampusManagerCard
              churchId={church.id}
              canAddCampus={subscription?.tier === 'connection'}
              upgradeUrl={`/churches/${slug}/billing`}
            />
          )}

          {/* Calendar feed */}
          {church.claimed && <CalendarFeedCard slug={slug ?? ''} />}
        </div>
      </div>

      {showBulletinImport && church && (
        <BulletinImportModal
          churchId={church.id}
          churchSlug={slug ?? ''}
          churchAddressText={church.addressLine1 ?? ''}
          churchLocationLat={church.locationLat ?? 0}
          churchLocationLng={church.locationLng ?? 0}
          onClose={() => setShowBulletinImport(false)}
        />
      )}
    </AdminShell>
  )
}
